import { useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Mail } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";

interface ContactsPanelProps {
  open: boolean;
  onClose: () => void;
}

const TelegramIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>
);

const departments = [
  { label: "Отдел продаж", href: "https://t.me/mveselkov" },
  { label: "Отдел маркетинга", href: "https://t.me/arturlichkid" },
  { label: "Отдел обучений", href: "https://t.me/Lud_AI" },
];

const ContactsPanel = ({ open, onClose }: ContactsPanelProps) => {
  useEffect(() => {
    if (open) {
      document.body.classList.add("contacts-panel-open");
    } else {
      document.body.classList.remove("contacts-panel-open");
    }
    return () => document.body.classList.remove("contacts-panel-open");
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: "100%" }}
          animate={{ x: 0 }}
          exit={{ x: "100%" }}
          transition={{ type: "tween", duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed top-0 right-0 bottom-0 w-full sm:w-[380px] z-[60] bg-background border-l border-border/50 shadow-[-8px_0_40px_-12px_rgba(0,0,0,0.15)] overflow-y-auto"
          aria-label="Контакты"
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-border/30">
            <h2 className="font-heading text-lg text-foreground">Контакты</h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Закрыть"
            >
              <X size={20} />
            </button>
          </div>

          <div className="px-6 py-6 space-y-8">
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Почта
              </p>
              <a
                href="mailto:wmt-ai@wmtgroup.ru"
                className="inline-flex items-center gap-2 text-base font-body text-foreground hover:text-accent transition-colors"
              >
                <Mail size={16} className="text-accent" />
                wmt-ai@wmtgroup.ru
              </a>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
                Telegram
              </p>
              <ul className="space-y-3">
                {departments.map((d) => (
                  <li key={d.href}>
                    <a
                      href={d.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 text-base font-body text-foreground hover:text-accent transition-colors"
                    >
                      <TelegramIcon className="w-4 h-4 text-accent shrink-0" />
                      <span className="underline-offset-4 group-hover:underline">
                        {d.label}
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <a
                href="https://t.me/nik0lchak"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2 text-base font-body text-accent hover:text-accent/80 transition-colors no-underline"
              >
                <TelegramIcon className="w-4 h-4 text-accent shrink-0" />
                <span className="no-underline">
                  Стань нашим партнёром
                </span>
              </a>
            </div>

            <div>
              <SocialLinks iconClassName="h-7 w-7" />
            </div>
          </div>
        </motion.aside>
      )}
    </AnimatePresence>
  );
};

export default ContactsPanel;
